import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './ServicesPageSettings.css';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const slugify = (value) =>
  (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const uniqueId = (prefix = 'id') =>
  `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;

function ServicesPageSettings() {
  const [heroTitle, setHeroTitle] = useState('');
  const [heroSubtitle, setHeroSubtitle] = useState('');
  const [pillars, setPillars] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [openPillar, setOpenPillar] = useState(0);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/services/page`);
        const d = res.data || {};
        setHeroTitle(d.heroTitle ?? '');
        setHeroSubtitle(d.heroSubtitle ?? '');
        setPillars(Array.isArray(d.pillars) ? d.pillars : []);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPage();
  }, []);

  const setPillar = (pillarIndex, field, value) => {
    setPillars((prev) => {
      const next = [...prev];
      next[pillarIndex] = { ...next[pillarIndex], [field]: value };
      return next;
    });
  };

  const setSubservice = (pillarIndex, subIndex, field, value) => {
    setPillars((prev) => {
      const next = [...prev];
      const sub = [...(next[pillarIndex].subservices || [])];
      sub[subIndex] = { ...sub[subIndex], [field]: value };
      next[pillarIndex] = { ...next[pillarIndex], subservices: sub };
      return next;
    });
  };

  const handleAddPillar = () => {
    setPillars((prev) => {
      const next = [
        ...prev,
        {
          id: uniqueId('pillar'),
          name: '',
          image: '',
          subservices: [],
        },
      ];
      return next;
    });
    setOpenPillar(pillars.length);
  };

  const handleDeletePillar = (pillarIndex) => {
    setPillars((prev) => prev.filter((_, i) => i !== pillarIndex));
    setOpenPillar((prev) => {
      if (prev === pillarIndex) return -1;
      if (prev > pillarIndex) return prev - 1;
      return prev;
    });
  };

  const handleAddSubservice = (pillarIndex) => {
    setPillars((prev) => {
      const next = [...prev];
      const current = next[pillarIndex];
      const subservices = [...(current.subservices || [])];
      subservices.push({
        id: uniqueId('sub'),
        label: '',
        description: '',
        image: '',
      });
      next[pillarIndex] = { ...current, subservices };
      return next;
    });
  };

  const handleDeleteSubservice = (pillarIndex, subIndex) => {
    setPillars((prev) => {
      const next = [...prev];
      const current = next[pillarIndex];
      const subservices = (current.subservices || []).filter(
        (_, i) => i !== subIndex
      );
      next[pillarIndex] = { ...current, subservices };
      return next;
    });
  };

  const handlePillarImageUpload = async (pillarIndex, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/services/page/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data && res.data.url) setPillar(pillarIndex, 'image', res.data.url);
    } catch (err) {
      console.error(err);
      setMessage('Upload failed.');
    }
  };

  const handleSubserviceImageUpload = async (pillarIndex, subIndex, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/services/page/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data && res.data.url) {
        setSubservice(pillarIndex, subIndex, 'image', res.data.url);
      }
    } catch (err) {
      console.error(err);
      setMessage('Upload failed.');
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage('');
    try {
      const normalizedPillars = (pillars || []).map((p) => {
        const pillarId = p.id || slugify(p.name) || uniqueId('pillar');
        const normalizedSubs = (p.subservices || []).map((s) => ({
          ...s,
          id: s.id || slugify(s.label) || uniqueId('sub'),
        }));
        return { ...p, id: pillarId, subservices: normalizedSubs };
      });

      await axios.put(`${API_BASE_URL}/api/services/page`, {
        heroTitle,
        heroSubtitle,
        pillars: normalizedPillars,
      });
      setPillars(normalizedPillars);
      setMessage('Services page updated successfully.');
    } catch (err) {
      console.error(err);
      setMessage('Unable to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <p className="services-page-settings-loading">Loading services page…</p>
    );
  }

  return (
    <div className="services-page-settings">
      <div className="services-page-settings-header">
        <div>
          <h2 className="services-page-settings-title">Services page</h2>
          <p className="services-page-settings-subtitle">
            Edit the /services page: hero title and subtitle, practice pillars,
            and subservices (labels, descriptions, images).
          </p>
        </div>
        <button
          type="button"
          className="services-page-settings-save-btn"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      <div className="services-page-settings-hero">
        <h3 className="services-page-settings-section-title">Hero</h3>
        <label className="services-page-settings-label">
          Hero title
          <input
            type="text"
            className="services-page-settings-input"
            value={heroTitle}
            onChange={(e) => setHeroTitle(e.target.value)}
            placeholder="Our Practice Areas"
          />
        </label>
        <label className="services-page-settings-label">
          Hero subtitle
          <textarea
            className="services-page-settings-textarea"
            rows={2}
            value={heroSubtitle}
            onChange={(e) => setHeroSubtitle(e.target.value)}
            placeholder="A curated suite of corporate and commercial services..."
          />
        </label>
      </div>

      <div className="services-page-settings-pillars">
        <div className="services-page-settings-title-row">
          <h3 className="services-page-settings-section-title">Practice pillars</h3>
          <button
            type="button"
            className="services-page-settings-secondary-btn"
            onClick={handleAddPillar}
          >
            + Add pillar
          </button>
        </div>
        {pillars.map((pillar, pIdx) => (
          <div key={pillar.id || pIdx} className="services-page-settings-pillar">
            <button
              type="button"
              className="services-page-settings-pillar-toggle"
              onClick={() => setOpenPillar(openPillar === pIdx ? -1 : pIdx)}
            >
              {openPillar === pIdx ? '▼' : '▶'} {pillar.name || 'Pillar ' + (pIdx + 1)}
            </button>
            {openPillar === pIdx && (
              <div className="services-page-settings-pillar-body">
                <div className="services-page-settings-actions-row">
                  <button
                    type="button"
                    className="services-page-settings-danger-btn"
                    onClick={() => handleDeletePillar(pIdx)}
                  >
                    Delete pillar
                  </button>
                </div>
                <label className="services-page-settings-label">
                  Pillar name
                  <input
                    type="text"
                    className="services-page-settings-input"
                    value={pillar.name || ''}
                    onChange={(e) => setPillar(pIdx, 'name', e.target.value)}
                    placeholder="Corporate Advisory"
                  />
                </label>
                <label className="services-page-settings-label">
                  Pillar image URL
                  <input
                    type="text"
                    className="services-page-settings-input"
                    value={pillar.image || ''}
                    onChange={(e) => setPillar(pIdx, 'image', e.target.value)}
                    placeholder="https://... or leave empty for default"
                  />
                </label>
                <label className="services-page-settings-label">
                  Or upload pillar image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const f = e.target.files && e.target.files[0];
                      if (f) handlePillarImageUpload(pIdx, f);
                    }}
                  />
                </label>

                <div className="services-page-settings-title-row">
                  <h4 className="services-page-settings-subsection-title">
                    Subservices
                  </h4>
                  <button
                    type="button"
                    className="services-page-settings-secondary-btn"
                    onClick={() => handleAddSubservice(pIdx)}
                  >
                    + Add subservice
                  </button>
                </div>
                {(pillar.subservices || []).map((sub, sIdx) => (
                  <div
                    key={sub.id || sIdx}
                    className="services-page-settings-subservice"
                  >
                    <div className="services-page-settings-actions-row">
                      <button
                        type="button"
                        className="services-page-settings-danger-btn"
                        onClick={() => handleDeleteSubservice(pIdx, sIdx)}
                      >
                        Remove
                      </button>
                    </div>
                    <label className="services-page-settings-label">
                      Label
                      <input
                        type="text"
                        className="services-page-settings-input"
                        value={sub.label || ''}
                        onChange={(e) =>
                          setSubservice(pIdx, sIdx, 'label', e.target.value)
                        }
                      />
                    </label>
                    <label className="services-page-settings-label">
                      Description
                      <textarea
                        className="services-page-settings-textarea"
                        rows={3}
                        value={sub.description || ''}
                        onChange={(e) =>
                          setSubservice(pIdx, sIdx, 'description', e.target.value)
                        }
                      />
                    </label>
                    <label className="services-page-settings-label">
                      Image URL
                      <input
                        type="text"
                        className="services-page-settings-input"
                        value={sub.image || ''}
                        onChange={(e) =>
                          setSubservice(pIdx, sIdx, 'image', e.target.value)
                        }
                      />
                    </label>
                    <label className="services-page-settings-label">
                      Or upload
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const f = e.target.files && e.target.files[0];
                          if (f) handleSubserviceImageUpload(pIdx, sIdx, f);
                        }}
                      />
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {message && (
        <p className="services-page-settings-message">{message}</p>
      )}
    </div>
  );
}

export default ServicesPageSettings;
